import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Products = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  const defaultCategories = [
    {
      id: 'tech',
      name: 'Tech Gadgets',
      slug: 'tech-gadgets',
      description: 'Innovative tech accessories for modern workplaces',
      image_url: 'https://images.unsplash.com/photo-1625948515291-69613efd103f?auto=format&fit=crop&q=80'
    },
    {
      id: 'drinkware',
      name: 'Drinkware',
      slug: 'drinkware',
      description: 'Custom branded bottles, mugs, and tumblers',
      image_url: 'https://images.unsplash.com/photo-1523294587484-bae6cc870010?auto=format&fit=crop&q=80'
    },
    {
      id: 'apparel',
      name: 'Apparel & Merchandise',
      slug: 'apparel',
      description: 'Custom branded clothing and wearables',
      image_url: 'https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?auto=format&fit=crop&q=80'
    },
    {
      id: 'office',
      name: 'Office & Desk Accessories',
      slug: 'office-accessories',
      description: 'Professional desk essentials and stationery',
      image_url: 'https://images.unsplash.com/photo-1758188753373-5b01a0fc6d9d?auto=format&fit=crop&q=80'
    },
    {
      id: 'eco',
      name: 'Eco-Friendly Gifts',
      slug: 'eco-friendly',
      description: 'Sustainable and environmentally conscious products',
      image_url: 'https://images.unsplash.com/photo-1719622144274-2408ecf6f6d9?auto=format&fit=crop&q=80'
    },
    {
      id: 'luxury',
      name: 'Luxury Hampers',
      slug: 'luxury-hampers',
      description: 'Premium curated gift hampers for special occasions',
      image_url: 'https://images.unsplash.com/photo-1634283715079-d91bbed0ece0?auto=format&fit=crop&q=80'
    },
    {
      id: 'travel',
      name: 'Travel Accessories',
      slug: 'travel-accessories',
      description: 'Essentials for business travelers',
      image_url: 'https://images.unsplash.com/photo-1530281700549-e82e7bf110d6?auto=format&fit=crop&q=80'
    },
    {
      id: 'stationery',
      name: 'Stationery',
      slug: 'stationery',
      description: 'Premium notebooks, pens, and writing instruments',
      image_url: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&q=80'
    },
  ];

  useEffect(() => {
    fetchData();
  }, [selectedCategory]);

  const fetchData = async () => {
    try {
      const categoriesRes = await axios.get(`${API}/categories`);
      const fetchedCategories = categoriesRes.data.length > 0 ? categoriesRes.data : defaultCategories;
      setCategories(fetchedCategories);

      const productsRes = await axios.get(`${API}/products${
        selectedCategory !== 'all' ? `?category=${selectedCategory}` : ''
      }`);
      setProducts(productsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setCategories(defaultCategories);
      setProducts([]);
      setLoading(false);
    }
  };

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(p => p.category_slug === selectedCategory);

  return (
    <div>
      <section className="py-24 md:py-32 bg-gradient-to-br from-surface via-background to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="text-xs font-medium tracking-widest uppercase text-muted mb-4" data-testid="products-label">
              Our Collection
            </div>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-tight mb-6" data-testid="products-title">
              Product <span className="text-accent">Categories</span>
            </h1>
            <p className="text-lg md:text-xl text-muted max-w-3xl mx-auto" data-testid="products-description">
              Explore our comprehensive range of premium corporate gifts and promotional merchandise.
            </p>
          </motion.div>

          <div className="flex flex-wrap gap-3 justify-center mb-12">
            <button
              onClick={() => setSelectedCategory('all')}
              data-testid="category-filter-all"
              className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${
                selectedCategory === 'all'
                  ? 'bg-accent text-white shadow-lg shadow-accent/20'
                  : 'bg-white border border-slate-200 text-muted hover:border-accent'
              }`}
            >
              All Categories
            </button>
            {categories.map((category) => (
              <button
                key={category.id || category.slug}
                onClick={() => setSelectedCategory(category.slug)}
                data-testid={`category-filter-${category.slug}`}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-200 ${
                  selectedCategory === category.slug
                    ? 'bg-accent text-white shadow-lg shadow-accent/20'
                    : 'bg-white border border-slate-200 text-muted hover:border-accent'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.id || category.slug}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="group relative overflow-hidden rounded-xl bg-white border border-slate-100 hover:shadow-2xl transition-all duration-300 cursor-pointer"
                data-testid={`category-card-${index}`}
                onClick={() => setSelectedCategory(category.slug)}
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={category.image_url}
                    alt={category.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    data-testid={`category-image-${index}`}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-2" data-testid={`category-name-${index}`}>{category.name}</h3>
                  <p className="text-sm text-muted" data-testid={`category-description-${index}`}>{category.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {products.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="mt-16"
            >
              <h2 className="text-3xl font-semibold mb-8 text-center" data-testid="featured-products-title">
                {selectedCategory === 'all' ? 'Featured Products' : `${categories.find(c => c.slug === selectedCategory)?.name || ''} Products`}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {filteredProducts.map((product, index) => (
                  <div
                    key={product.id}
                    className="bg-white border border-slate-100 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300"
                    data-testid={`product-card-${index}`}
                  >
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-48 object-cover"
                      data-testid={`product-image-${index}`}
                    />
                    <div className="p-4">
                      <h3 className="font-semibold mb-1" data-testid={`product-name-${index}`}>{product.name}</h3>
                      <p className="text-sm text-muted" data-testid={`product-description-${index}`}>{product.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {products.length === 0 && !loading && (
            <div className="text-center mt-16 p-12 bg-surface rounded-2xl" data-testid="no-products-message">
              <p className="text-muted text-lg">No products available in this category yet. Check back soon!</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Products;
